import React, { useState, useEffect } from 'react';
import { X, Download, FileSpreadsheet } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { IngredientExportFilters } from '../utils/osExcel';

interface IngredientsExportFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (filters: IngredientExportFilters) => void;
  isExporting?: boolean;
  currentSearch?: string;
}

const STATUS_OPTIONS: { value: 'active' | 'inactive' | 'export' | 'development'; labelKey: string }[] = [
  { value: 'active', labelKey: 'status.active' },
  { value: 'inactive', labelKey: 'status.inactive' },
  { value: 'export', labelKey: 'status.export' },
  { value: 'development', labelKey: 'status.development' },
];

const RNExportFilterModal: React.FC<IngredientsExportFilterModalProps> = ({
  isOpen,
  onClose,
  onExport,
  isExporting = false,
  currentSearch = '',
}) => {
  const { t } = useTranslation();

  const [status, setStatus] = useState<'active' | 'inactive' | 'export' | 'development' | null>(null);
  const [search, setSearch] = useState<string>('');

  // Reset filters and pre-fill search when modal opens
  useEffect(() => {
    if (isOpen) {
      setStatus(null);
      setSearch(currentSearch);
    }
  }, [isOpen, currentSearch]);

  // Handle keyboard events
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleExport = () => {
    onExport({ status, search: search || undefined });
  };

  const hasFilters = status !== null || search.trim() !== '';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center backdrop-blur-sm z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="text-emerald-600" size={24} />
            <h2 className="text-lg font-semibold text-gray-800">
              {t('excel.export.filterTitleIngredients')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={isExporting}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-5">
          {/* Search filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('excel.export.filterBySearch')}
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('common.search') || 'Search...'}
              className="w-full h-9 border border-gray-300 rounded-lg px-3 text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all duration-200"
              disabled={isExporting}
            />
          </div>

          {/* Status filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('excel.export.filterByStatus') || 'Filter by Status'}
            </label>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map(option => (
                <button
                  key={option.value}
                  onClick={() => setStatus(status === option.value ? null : option.value)}
                  className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                    status === option.value
                      ? 'bg-emerald-100 border-emerald-500 text-emerald-700'
                      : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                  }`}
                  disabled={isExporting}
                >
                  {t(option.labelKey) || option.value}
                </button>
              ))}
            </div>
          </div>

          {/* Info text */}
          <div className="text-sm text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
            {hasFilters
              ? t('excel.export.filtersApplied') || 'Filters applied - will export matching items'
              : t('excel.export.noFiltersApplied') || 'No filters applied - will export all items'}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isExporting}
          >
            {t('buttons.cancel') || 'Cancel'}
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg flex items-center gap-2 transition-all hover:opacity-90 disabled:opacity-50"
            style={{ background: 'linear-gradient(to right, var(--color-primary), var(--color-secondary))' }}
          >
            {isExporting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>{t('excel.export.exporting') || 'Exporting...'}</span>
              </>
            ) : (
              <>
                <Download size={16} />
                <span>{t('excel.export.export') || 'Export'}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RNExportFilterModal;
